import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API } from 'src/config';
import { DeviceStore } from './DeviceStore';

export interface AppDeviceSettings {
  id: number;
  device_id: string;
  user_id: number;
  device_name: string;
  serial_number: string;
  field1?: string;
  field2?: string;
  parent_device_id?: string;
  location: number; // 1=Bed, 2=Chair, 3=Sofa, 4=Floor
  notifications: boolean;
  presence_notice?: boolean;
  absence_notice?: boolean;
  night_time?: boolean;
  night_start?: string; // Format: "2200"
  night_end?: string; // Format: "0800"
  night_delay?: number; // minutes
  day_time?: boolean;
  day_delay?: number; // minutes
  turning?: boolean;
  turning_delay?: number;
  sitting?: boolean;
  sitting_period?: number;
  restless?: boolean;
  fast_movement?: boolean;
  intention_to_leave?: boolean;
  no_movement?: boolean;
  no_movement_delay?: number;
  send_delayed_bed_notifications?: boolean;
  evening_days?: number; // Bitmask for delayed bed notifications (default 127 = all days)
  morning_days?: number; // Bitmask for delayed bed notifications (default 127 = all days)
  only_movement_detection?: boolean; // Use PIR sensor as motion sensor only
  notifications_silenced_until?: string | null;
  allow_notification_muting?: boolean;
  movement_alert_cooldown_mins?: number;
  motion_silenced?: boolean;
  motion_silence_mode?: number;
  turning_delay_day?: number;
  created_at?: Date;
  updated_at?: Date;
}

export interface RegisterDeviceData {
  serialNumber: string;
  pin: string;
  deviceName: string;
  field1?: string;
  field2?: string;
  groupIds?: number[];
}

/** POST /v1/device answers with the device (qs-status DeviceResponse). */
interface RegisterDeviceResponse {
  device_id?: string;
}

export interface UpdateDeviceData {
  deviceName?: string;
  field1?: string;
  field2?: string;
}

class DevicesStoreClass {
  devices: DeviceStore[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get hasChanges(): boolean {
    return this.devices.some((device) => device.isDirty);
  }

  get changedDevices(): DeviceStore[] {
    return this.devices.filter((device) => device.isDirty);
  }

  get sortedDevices(): DeviceStore[] {
    return [...this.devices].sort((a, b) =>
      a.deviceName.localeCompare(b.deviceName)
    );
  }

  async fetchDevices(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const devices = await EmfitAPI.get<AppDeviceSettings[]>(
        `${STATUS_API}/v1/device`
      );
      runInAction(() => {
        // Wrap in DeviceStore instances
        this.devices = (devices || []).map(
          (settings) => new DeviceStore(settings)
        );
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch devices';
        this.loading = false;
      });
      console.error('Failed to fetch devices:', error);
    }
  }

  async registerDevice(data: RegisterDeviceData): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      // qs-status validates `serial` + `pin` (AddDeviceRequest) and answers with
      // a DeviceResponse.
      const result = await EmfitAPI.post<RegisterDeviceResponse>(
        `${STATUS_API}/v1/device`,
        {
          body: JSON.stringify({
            serial: data.serialNumber,
            pin: data.pin,
            device_name: data.deviceName,
            field1: data.field1 ?? '',
            field2: data.field2 ?? '',
          }),
        },
      );

      const deviceId = result?.device_id;
      if (!deviceId) {
        throw new Error('The information you entered is invalid or already in use.');
      }

      if (data.groupIds && data.groupIds.length > 0) {
        await Promise.all(
          data.groupIds.map((groupId) => this.assignDeviceToGroup(deviceId, groupId)),
        );
      }

      runInAction(() => {
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to register device';
        this.loading = false;
      });
      console.error('Failed to register device:', error);
      return false;
    }
  }

  async assignDeviceToGroup(deviceId: string, groupId: number): Promise<boolean> {
    this.error = null;
    try {
      await EmfitAPI.post(`${STATUS_API}/v1/group-device`, {
        body: JSON.stringify({ group_id: groupId, device_id: deviceId }),
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to assign device to group';
      });
      console.error(`Failed to assign device ${deviceId} to group ${groupId}:`, error);
      return false;
    }
  }

  /**
   * Unassign a device from a group.
   *
   * Takes the sb_group_devices PAIR id (Group.devices[].id), not the device
   * id - DELETE /v1/group-device/:id resolves the pair by its own primary key.
   */
  async removeDeviceFromGroupPair(pairId: number): Promise<boolean> {
    this.error = null;
    try {
      await EmfitAPI.delete<void>(`${STATUS_API}/v1/group-device/${pairId}`);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to remove device from group';
      });
      console.error(`Failed to remove group-device pair ${pairId}:`, error);
      return false;
    }
  }

  async saveDevice(deviceId: string, data: UpdateDeviceData): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.put(`${STATUS_API}/v1/device/${deviceId}`, {
        body: JSON.stringify({
          device_name: data.deviceName,
          field1: data.field1,
          field2: data.field2,
        }),
      });
      runInAction(() => {
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to save device';
        this.loading = false;
      });
      console.error('Failed to save device:', error);
      return false;
    }
  }

  async saveDeviceSettings(): Promise<boolean> {
    const changedDevices = this.changedDevices;
    if (changedDevices.length === 0) {
      return true;
    }

    this.loading = true;
    this.error = null;

    try {
      // Convert DeviceStore instances to settings objects for API
      const changes = changedDevices.map((device) =>
        device.getDeviceSettings()
      );
      await EmfitAPI.put(`${STATUS_API}/v1/device`, {
        body: JSON.stringify(changes),
      });
      runInAction(() => {
        // Mark all changed devices as saved
        changedDevices.forEach((device) => device.markAsSaved());
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to save device settings';
        this.loading = false;
      });
      console.error('Failed to save device settings:', error);
      return false;
    }
  }

  async addDeviceToDevice(
    parentDeviceId: string,
    childDeviceId: string
  ): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.post(`${STATUS_API}/v1/device/${parentDeviceId}/add`, {
        body: JSON.stringify({ device_id: childDeviceId }),
      });
      runInAction(() => {
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to add device';
        this.loading = false;
      });
      return false;
    }
  }

  async deleteDeviceFromDevice(deviceId: string): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.delete(`${STATUS_API}/v1/device/${deviceId}/remove`);
      runInAction(() => {
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to remove device';
        this.loading = false;
      });
      return false;
    }
  }

  async deleteDeviceFromAccount(serialNumber: string): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.post(`${STATUS_API}/v1/device/delete-from-account`, {
        body: JSON.stringify({ serial: serialNumber }),
      });
      runInAction(() => {
        this.loading = false;
      });
      await this.fetchDevices();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to delete device';
        this.loading = false;
      });
      return false;
    }
  }

  getDeviceById(deviceId: string): DeviceStore | undefined {
    return this.devices.find((d) => d.deviceId === deviceId);
  }

  getDevicesByParent(devices?: DeviceStore[]): Record<string, DeviceStore[]> {
    const devicesToGroup = devices || this.sortedDevices;
    const grouped: Record<string, DeviceStore[]> = {};

    for (const deviceStore of devicesToGroup) {
      const device = deviceStore.device;
      const key = device.parent_device_id || device.device_id;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(deviceStore);
    }

    // Sort so parent is first
    Object.values(grouped).forEach((group) => {
      group.sort((a) => (a.device.parent_device_id ? 1 : -1));
    });

    return grouped;
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.devices = [];
    this.loading = false;
    this.error = null;
  }
}

export const DevicesStore = new DevicesStoreClass();
